// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './BaleFeedMonitor.scss';

import { collection, query, limit, orderBy, where, onSnapshot, doc } from 'firebase/firestore';
import { firestore } from '../../context/firebase';
import { format } from 'date-fns';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import BaleCard from '../BaleCard/BaleCard';
import ObservationModal from '../ObservationModal/ObservationModal';

import speed_icon_white from '../../assets/icons/white/speed.png';
import speed_icon_gray from '../../assets/icons/gray/speed.png';
import {
  DEVICE_VOLUMES_DOCUMENT_PATH,
  OBSERVATIONS_COLLECTION_PATH,
} from '../../shared_components/utils/FirebaseUtils';
import { DEVICE, SUPPRESSED, TIMESTAMP } from '../../constant/firebase';
import { useAuthStore } from '../../stores/authStore';

export default function BaleFeedMonitor() {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserDevices = useAuthStore((state) => state.currentUser?.devices);
  const [observations, setObservations] = React.useState({});
  // eslint-disable-next-line no-unused-vars
  const [currentTime, setCurrentTime] = React.useState(new Date());
  const [activeObservation, setActiveObservation] = React.useState(null);
  const unsubscribes = React.useRef({});
  const [velocities, setVelocities] = React.useState({});
  const velocityBufferRef = React.useRef({});
  const velocityUpdateInterval = 5; // Todo: Include this in firebase config

  React.useEffect(() => {
    /* Subscribe to the last observations for each device */
    const NUM_OBSERVATIONS = 20;
    const observationsRef = collection(firestore, OBSERVATIONS_COLLECTION_PATH(currentUserClientId));
    for (const deviceId of Object.keys(currentUserDevices ?? {})) {
      // Unsubscribe from the previous listener
      if (unsubscribes.current[deviceId]) {
        unsubscribes.current[deviceId]();
      }

      // Subscribe to the new listener
      const observationsQuery = query(
        observationsRef,
        where(DEVICE, '==', deviceId),
        where(SUPPRESSED, '==', false),
        orderBy(TIMESTAMP, 'desc'),
        limit(NUM_OBSERVATIONS),
      );

      const unsubscribeNew = onSnapshot(observationsQuery, (snapshot) => {
        const observationsData = {};
        snapshot.forEach((doc) => {
          const data = { ...doc.data(), doc_id: doc.id };
          observationsData[doc.id] = data;
        });
        setObservations((prev) => {
          return { ...prev, [deviceId]: observationsData };
        });
      });
      unsubscribes.current[deviceId] = unsubscribeNew;
    }
  }, []);

  // Update the average velocity for each device
  // Velocity is computed as the average of the last x updates (x = velocityUpdateInterval)
  React.useEffect(() => {
    const volumeUnsubscribes = {};
    const VOLUME_DOC_ID = 'volume_tracking';
    for (const deviceId of Object.keys(currentUserDevices ?? {})) {
      // Initialize the buffer for each device to store the last x values (x = velocityUpdateInterval)
      if (!velocityBufferRef.current[deviceId]) {
        velocityBufferRef.current[deviceId] = { values: [], initialDisplayed: false };
      }

      const volumesDocRef = doc(firestore, DEVICE_VOLUMES_DOCUMENT_PATH(currentUserClientId, deviceId, VOLUME_DOC_ID));

      const unsubscribe = onSnapshot(volumesDocRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const newValue = data.current_belt_velocity;
          const unit = data.belt_velocity_units;
          const lastAlive = new Date();
          const lastUpdate = data.last_update.toDate();

          // check if conveyer belt is standing still
          const standing = lastUpdate && new Date() - lastUpdate > 60000;

          // Use the buffer object from the ref.
          const bufferObj = velocityBufferRef.current[deviceId];

          // If the conveyer belt is standing still, set the average to 0.
          if (standing) {
            setVelocities((prev) => ({
              ...prev,
              [deviceId]: { avg: 0, unit: unit, lastAlive: lastAlive },
            }));
            bufferObj.values = [];
            bufferObj.initialDisplayed = false;
          } else {
            /// If no value is shown yet for this device, update immediately.
            if (!bufferObj.initialDisplayed) {
              setVelocities((prev) => ({
                ...prev,
                [deviceId]: { avg: newValue, unit: unit, lastAlive: lastAlive },
              }));
              bufferObj.initialDisplayed = true;
              bufferObj.values.push(newValue);
            } else {
              // For subsequent updates, only add to the buffer.
              bufferObj.values.push(newValue);
            }

            // When the buffer reaches the desired length, compute the average.
            if (bufferObj.values.length === velocityUpdateInterval) {
              const sum = bufferObj.values.reduce((acc, val) => acc + val, 0);
              const avg = sum / velocityUpdateInterval;
              setVelocities((prev) => ({
                ...prev,
                [deviceId]: { avg: avg, unit: unit, lastAlive: lastAlive },
              }));
              // Clear the buffer for the next set of updates.
              bufferObj.values = [];
            }
          }
        }
      });
      volumeUnsubscribes[deviceId] = unsubscribe;
    }
    return () => {
      Object.values(volumeUnsubscribes).forEach((unsubscribe) => unsubscribe());
    };
  }, [currentUserDevices, currentUserClientId]);

  // Check periodically for stale velocity data, if NotAlive for more than 1 minute, set avg to undefined
  React.useEffect(() => {
    const CheckStaleData = () => {
      setVelocities((prev) => {
        const updated = { ...prev };
        Object.entries(updated).forEach(([deviceId, data]) => {
          if (data && data.lastAlive instanceof Date) {
            const isStale = Date.now() - data.lastAlive.getTime() > 60000;
            if (isStale) {
              updated[deviceId] = { ...data, avg: undefined };
            }
          }
        });
        return updated;
      });
    };

    const interval = setInterval(CheckStaleData, 30000);
    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    /* Update the current time every minute and trigger a re-render */
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Clean up the interval when the component unmounts
    return () => clearInterval(interval);
  }, []);

  function scrollObservation(scrollAmount) {
    /* Change the displayed observation by scrolling through scrollAmount observations. */
    if (!activeObservation || !scrollAmount) return;

    const observationIds = Object.keys(observations[activeObservation.device]).sort();
    const currentIndex = observationIds.indexOf(activeObservation.doc_id);
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + scrollAmount;
    if (nextIndex >= observationIds.length || nextIndex < 0) return;
    const nextObservationId = observationIds[nextIndex];
    setActiveObservation(observations[activeObservation.device][nextObservationId]);
  }

  const baleFeedMonitorHeight = Math.min(Object.keys(currentUserDevices ?? {}).length, isMobileOnly ? 1 : 2) * 9;

  return (
    <div
      className='bale-feed-monitor'
      style={{ minHeight: baleFeedMonitorHeight + 'rem', maxHeight: baleFeedMonitorHeight + 'rem' }}
    >
      <div className='bale-feed-monitor-lanes-container'>
        {currentUserDevices &&
          Object.keys(currentUserDevices).length > 0 &&
          Object.keys(currentUserDevices).map((deviceId) => {
            // Don't render the lane if there are no observations
            if (!observations[deviceId] || Object.keys(observations[deviceId]).length === 0) {
              return null;
            }

            return (
              <div className='bale-feed-monitor-lane' key={deviceId}>
                <div className='bale-feed-monitor-lane-header'>
                  <div className='bale-feed-monitor-lane-title'>
                    {currentUserDevices[deviceId]?.display_name ?? deviceId}
                  </div>
                  <div
                    className={`bale-feed-monitor-lane-velocity ${
                      !velocities[deviceId] || velocities[deviceId].avg === 0 || velocities[deviceId].avg === undefined
                        ? 'bale-feed-monitor-lane-velocity-gray'
                        : ''
                    }`}
                  >
                    <img
                      src={
                        !velocities[deviceId] ||
                        velocities[deviceId].avg === 0 ||
                        velocities[deviceId].avg === undefined
                          ? speed_icon_gray
                          : speed_icon_white
                      }
                      className='bale-feed-monitor-lane-velocity-icon'
                    />
                    <p className='bale-feed-monitor-lane-velocity-text'>
                      {!velocities[deviceId] || velocities[deviceId].avg === undefined ? (
                        t('bale_feed_no_data')
                      ) : (
                        <>
                          {Math.round(velocities[deviceId].avg * 100) / 100}
                          <span className='bale-feed-monitor-lane-velocity-unit'>{velocities[deviceId].unit}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className='bale-feed-monitor-lane-observations'>
                  {Object.entries(observations[deviceId]).map(([observationId, observation]) => {
                    const minElapsed = Math.floor(Math.max(0, (currentTime - observation.timestamp.toDate()) / 60000));
                    return (
                      <div
                        className='bale-feed-monitor-bale'
                        onClick={() => {
                          setActiveObservation(observation);
                        }}
                        key={observationId}
                      >
                        <BaleCard imageStoragePath={observation.image_storage_path} />
                        <div className='bale-feed-monitor-bale-time'>
                          {minElapsed < 60 ? `-${minElapsed}min` : format(observation.timestamp.toDate(), 'HH:mm')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>
      {activeObservation && (
        <ObservationModal
          observation={activeObservation}
          onHide={() => setActiveObservation(null)}
          scrollObservation={scrollObservation}
        />
      )}
    </div>
  );
}
